import * as XLSX from 'xlsx';
import { toPng } from 'html-to-image';
import { Chart } from 'chart.js';


export const downloadExcel = (data, filename, country, firstYear, lastYear) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${country}_${filename}-${firstYear}_${lastYear}.xlsx`);
};

export const downloadCSV = (date, data, filename, country, firstYear, lastYear,) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const formattedDate = new Date(date).toISOString().split('T')[0];

    XLSX.writeFile(workbook, `${formattedDate}_${filename}_${country}_${firstYear}_${lastYear}.csv`, { bookType: 'csv' });
};

const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// SheetJS's json_to_sheet builds one object per cell, which for very large,
// many-indicator bulk exports (hundreds of thousands of rows) can exhaust
// browser memory (RangeError: Invalid array length). This writes the CSV as
// plain text instead, which scales to much larger datasets.
export const downloadLargeCSV = (date, data, filename, country, firstYear, lastYear) => {
    const headers = [];
    const seenHeaders = new Set();
    data.forEach((row) => {
        Object.keys(row).forEach((key) => {
            if (!seenHeaders.has(key)) {
                seenHeaders.add(key);
                headers.push(key);
            }
        });
    });

    // Kept as an array of row strings and handed straight to Blob rather than joined
    // into one string first: very large "select everything" exports can produce a CSV
    // longer than the JS engine's max string length (~512MB in V8, RangeError: Invalid
    // string length), while Blob concatenates its parts without that ceiling.
    const parts = ["\uFEFF" + headers.map(escapeCsvCell).join(",") + "\r\n"];
    data.forEach((row) => {
        parts.push(headers.map((header) => escapeCsvCell(row[header])).join(",") + "\r\n");
    });

    const blob = new Blob(parts, { type: "text/csv;charset=utf-8;" });
    const formattedDate = new Date(date).toISOString().split('T')[0];

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${formattedDate}_${filename}_${country}_${firstYear}_${lastYear}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
};

export const downloadPNG = (ref, filename, country, firstYear, lastYear, color, includeTitle = true) => {
    let hideElement = document.getElementById("ico-download");
    hideElement.style.visibility = 'hidden';

    const chartCanvas = ref.current.querySelector('canvas');
    const chartInstance = Chart.getChart(chartCanvas);

    if (!chartInstance) {
        console.error('Chart instance not found.');
        hideElement.style.visibility = 'visible';
        return;
    }

    // Remember the original title display state
    const originalTitleDisplay = chartInstance.options.plugins.title.display;
    
    // Set the chart title based on user preference
    chartInstance.options.plugins.title.display = includeTitle;
    
    chartInstance.update();

    // Add a small delay to ensure the chart is fully rendered
    setTimeout(() => {
        toPng(ref.current, {
            cacheBust: false,
            backgroundColor: color
        })
        .then((dataUrl) => {
            const link = document.createElement("a");
            link.download = `${country}_${filename}-${firstYear}_${lastYear}.png`;
            link.href = dataUrl;
            link.click();

            // restore the original title display state
            chartInstance.options.plugins.title.display = originalTitleDisplay;
            chartInstance.update();

            hideElement.style.visibility = 'visible';
        })
        .catch((err) => {
            console.log(err);
            // restore the original title display state in case of error
            chartInstance.options.plugins.title.display = originalTitleDisplay;
            chartInstance.update();
            hideElement.style.visibility = 'visible';
        });
    }, 100); // 100ms delay to ensure chart is rendered
}

// Helper function to format data like the DataTable component
// A yes/no indicator is a single answer for the latest year reported. As a series its 1 or 0
// was formatted as a number, which turned every "no" into an empty cell.
export const statusTableRow = (data, label = "") => {
    // InFARM reports enrolment with year "0": no year to head the column with.
    const year = Number(data.statusYear) > 0 ? String(data.statusYear) : "Status";
    return { year, row: { Label: data.info?.element || label, Unit: "Yes/No", [year]: data.datasets?.[0]?.data?.[0] === 1 ? "Yes" : "No" } };
};

const formatDataForTable = (data, originalData, title = "") => {
    if (data.isStatus) return [statusTableRow(data, title).row];

    const isStackedBarChart = Array.isArray(data.labels[0]);
    const isPyramid = Boolean(data.isPyramid);
    const unit = isPyramid ? 'Population' : data.info.unit;

    const years = isStackedBarChart
        ? data.labels.map((label) => label[0])
        : data.labels;

    return data.datasets.map((dataset) => {
        const row = { 
            Label: dataset.label,
            // Same precedence as the Unit column in DataTable, so the file matches the table.
            Unit: dataset.unit || unit
        };

        years.forEach((year, index) => {
            let yValue;

            if (isStackedBarChart || isPyramid) {
                yValue = Math.abs(dataset.data[index]);
            } else {
                const dataPoint = dataset.data.find((point) => point.x === year);
                yValue = dataPoint && !isNaN(dataPoint.y) ? dataPoint.y : "";
            }

            row[year] = yValue && !isNaN(yValue) ? Number(yValue).toFixed(2) : "";
        });

        return row;
    });
};

export const downloadFormattedExcel = (data, originalData, filename, country, firstYear, lastYear) => {
    const formattedData = formatDataForTable(data, originalData, filename);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${country}_${filename}-${firstYear}_${lastYear}.xlsx`);
};

export const downloadFormattedCSV = (data, originalData, date, filename, country, firstYear, lastYear) => {
    const formattedData = formatDataForTable(data, originalData, filename);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const formattedDate = new Date(date).toISOString().split('T')[0];

    XLSX.writeFile(workbook, `${formattedDate}_${filename}_${country}_${firstYear}_${lastYear}.csv`, { bookType: 'csv' });
};